/**
 * HR, inside Staff — STORY-181, HR-D1–HR-D15. The shapes `apps/api/src/hr`
 * returns, for the Team, Compliance and Me tabs.
 *
 * Everything but the contract and the certificates is worked out on read:
 * *today*, *leave left* and *needs attention* are never stored.
 */

export type HrToday = "IN" | "OFF" | "ON_LEAVE" | "SICK";
export type ContractType = "FULL_TIME" | "PART_TIME";
export type CertificateState = "OK" | "DUE" | "EXPIRED" | "NONE";
export type AttentionKind =
  "CERTIFICATE_EXPIRED" | "CERTIFICATE_DUE" | "RENEWAL_WAITING" | "PROBATION_ENDING";

export interface HrContract {
  contractType: ContractType | null;
  startedOn: string | null;
  /** Their last day — set in Accounting, read here (HR-D15). */
  endedOn: string | null;
  probationEndsOn: string | null;
  noticeWeeks: number | null;
  contractedMinutes: number | null;
}

/** The eight ABO/Rh groups (SP-D2). */
export type BloodType =
  "A_POS" | "A_NEG" | "B_POS" | "B_NEG" | "AB_POS" | "AB_NEG" | "O_POS" | "O_NEG";

export const BLOOD_TYPES: readonly BloodType[] = [
  "A_POS",
  "A_NEG",
  "B_POS",
  "B_NEG",
  "AB_POS",
  "AB_NEG",
  "O_POS",
  "O_NEG",
];

export const BLOOD_TYPE_LABELS: Record<BloodType, string> = {
  A_POS: "A+",
  A_NEG: "A−",
  B_POS: "B+",
  B_NEG: "B−",
  AB_POS: "AB+",
  AB_NEG: "AB−",
  O_POS: "O+",
  O_NEG: "O−",
};

export interface HrEmergencyContact {
  name: string;
  relation: string | null;
  phoneE164: string | null;
}

/**
 * Personal details (SP-D1–SP-D4, STORY-192). **The blood type is health data**:
 * the server sends it only to an administrator or the person themselves, and
 * otherwise sends `null` with `bloodTypeHidden` — so whether one is recorded
 * is not given away either (SP-D3).
 */
export interface HrPersonal {
  dateOfBirth: string | null;
  bloodType: BloodType | null;
  bloodTypeHidden: boolean;
  emergencyContact: HrEmergencyContact | null;
}

export interface HrLeave {
  year: number;
  entitlementDays: number | null;
  takenDays: number;
  approvedDays: number;
  pendingDays: number;
  remainingDays: number | null;
}

export interface HrPendingRenewal {
  id: string;
  expiresOn: string | null;
  uploadedAt: string;
  hasFile: boolean;
}

export interface HrCertificate {
  id: string;
  name: string;
  neededToTreat: boolean;
  /** The date in force. A pending renewal does not move it. */
  expiresOn: string | null;
  state: CertificateState;
  pendingRenewal: HrPendingRenewal | null;
  /** The last upload was rejected (HR-D11), until the next upload clears it. */
  rejection: { at: string; reason: string | null } | null;
}

export interface HrAttention {
  kind: AttentionKind;
  certificate: string | null;
  date: string | null;
  neededToTreat: boolean;
}

export interface HrPerson {
  userId: string;
  name: string;
  role: string;
  today: HrToday;
  /** The locations they are rostered in at today; empty when off, on leave or sick. */
  inAt: string[];
  locations: { id: string; name: string }[];
  contract: HrContract;
  personal: HrPersonal;
  leave: HrLeave;
  certificates: HrCertificate[];
  attention: HrAttention[];
}

export interface HrTeam {
  today: {
    total: number;
    in: number;
    off: number;
    onLeave: number;
    sick: number;
    /** Each location whose front-desk people are all out today (HR-D13). */
    noFrontDeskAt: { branchId: string; name: string }[];
  };
  warnDays: number;
  people: HrPerson[];
}

export interface UpdateContract {
  contractType?: ContractType | null;
  probationEndsOn?: string | null;
  noticeWeeks?: number | null;
}

/** Every field optional, `null` clears it. The contact is name, relation and phone together. */
export interface UpdatePersonal {
  dateOfBirth?: string | null;
  bloodType?: BloodType | null;
  emergencyContact?: HrEmergencyContact | null;
}

/** One certificate on the clinic's list, with whose it is. */
export interface HrClinicCertificate extends HrCertificate {
  userId: string;
  personName: string;
  role: string;
}

export type RenewalMimeType = "application/pdf" | "image/jpeg" | "image/png";

export const RENEWAL_MIME_TYPES: readonly RenewalMimeType[] = [
  "application/pdf",
  "image/jpeg",
  "image/png",
];

/** A renewal's document is capped at 10 MB (STORY-181b). */
export const MAX_RENEWAL_BYTES = 10 * 1024 * 1024;

export interface RenewalFile {
  s3Key: string;
  fileName: string;
  mimeType: RenewalMimeType;
  sizeBytes: number;
}

export interface CreateRenewal {
  /** `YYYY-MM-DD`. Required, and later than the one in force, when it runs out (HR-D10). */
  expiresOn?: string;
  file?: RenewalFile;
}

/** The clinic's warning window, in days (HR-D7): 1–365, default 30. */
export interface HrSettings {
  warnDays: number;
}
